import * as React from 'react';
import {
  Button,
  PageSection,
  Title
} from '@patternfly/react-core';
import { buildTimeBranding, useSiteConfig } from '../config/SiteConfigContext';
import msSignInImage from '../bgimages/MSsignIn.png';
import msPermissionsImage from '../bgimages/MSpermissions.png';
import sharePointURLImage from '../bgimages/SharePointURL.png';
import requestStatusImage from '../bgimages/RequestStatus.png';

export interface ISupportProps {
  sampleProp?: string;
}

// eslint-disable-next-line prefer-const
let Support: React.FunctionComponent<ISupportProps> = () => {
  const site = useSiteConfig();
  const PRODUCT_NAME = site?.productName || buildTimeBranding.applicationName || 'QuickCheck';
  const SUPPORT_EMAIL = site?.supportEmail || '';
  return (
  <PageSection hasBodyWrapper={false}>
    <Title headingLevel="h1" size="xl">{PRODUCT_NAME} - How it works</Title>

      <p>
      {PRODUCT_NAME} is designed to give rapid and actionable recommendations about how you can improve your cloud security posture, in just a few clicks.
      </p>
      <p>
      {PRODUCT_NAME} works using Microsoft single-sign on protocols, so you can share data about the configuration of your Microsoft (and other) environments for your selected partner organisation to complete the assessment.
      </p>

    <Title headingLevel="h2">1 - Sign-in and delegated permissions <i className="pf-icon fa-download"></i></Title>

    <div style={{ marginLeft: '1.5rem', lineHeight: 2 }}>
      <p>
      First, {PRODUCT_NAME} needs to be provisioned access to your Microsoft environment to collect data.
      When you select a check from the home page, you are redirected to Microsoft to sign in and consent for just the permissions required for that check.
      </p>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <img
          src={msSignInImage}
          alt="Microsoft sign-in screen"
          style={{ width: '100%', maxWidth: '360px', height: 'auto', display: 'block' }}
        />
      </div>
      <br/>
      <p>
        Checks use app registrations with <strong>delegated</strong> API permissions only,
        so access is tied to a signed-in user context rather than broad, app-only tenant access.
      </p>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <img
          src={msPermissionsImage}
          alt="Microsoft permissions requested screen"
          style={{ width: '100%', maxWidth: '360px', height: 'auto', display: 'block' }}
        />
      </div>
      <br/>
      <p>
        For SharePoint checks, you are also asked for the SharePoint site URL up front so collection is targeted to the
        specific site you provide.
      </p>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <img
          src={sharePointURLImage}
          alt="SharePoint URL screen"
          style={{ width: '100%', maxWidth: '360px', height: 'auto', display: 'block' }}
        />
      </div>
      <br/>
      Click “Contact us” on the {PRODUCT_NAME} portal to find out more about the permissions and what they are required for.
    </div>

    <Title headingLevel="h2" >2 - Secure collection</Title>

    <div style={{ marginLeft: '1.5rem', lineHeight: 2 }}>
      <p>
        After Microsoft returns authentication details, the collection service validates the token and starts a dedicated
        background job for your request.
      </p>
      <p>
        Each check is tracked with a unique request ID, and only authorised users can view that request&apos;s collection status.
      </p>
    </div>

    <Title headingLevel="h2" >3 - Live status page</Title>

    <div style={{ marginLeft: '1.5rem', lineHeight: 2 }}>
      <p>
        Once the check has started, you are redirected to the status page which shows a summary of all the checks you have requested.
      </p>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <img
          src={requestStatusImage}
          alt="Request status page"
          style={{ width: '100%', maxWidth: '800px', height: 'auto', display: 'block' }}
        />
      </div>
      <p>
        Clicking on a check will show you the detailed status of that check, including a timestamped activity feed. This page auto-refreshes and can be manually refreshed, giving your team clear visibility that the check is running and
        where it is in the process of being completed.
      </p>
    </div>

    <Title headingLevel="h2" >4 - Reporting</Title>

    <div style={{ marginLeft: '1.5rem', lineHeight: 2 }}>
      <p>
        Once analysis is complete, findings are made available for review.
        Our team will help you interpret the results, providing recommendations and practical next steps.
      </p>
    </div>

      {SUPPORT_EMAIL ? (
        <p>
          If you need any further assistance just{' '}
          <Button variant="primary" component="a" href={`mailto:${SUPPORT_EMAIL}?subject=${PRODUCT_NAME} support`}>
            Contact Support
          </Button>
        </p>
      ) : null}

  </PageSection>
);
};

export { Support };
